import React, { useState, useEffect } from 'react'
import styles from './ITPage.module.scss'
import { getITServices, getITProjects, createITProject } from '../../../service/ITService'
import Pagination from '../../../components/Pagination/Pagination'
import Loader from '../../../components/Loader/Loader'
import ServiceRequestModal from '../../../components/ServiceRequestModal/ServiceRequestModal'
import { ComputerIcon, StarIcon, PeopleIcon, MoneyIcon, CheckIcon, PlusIcon, EditIcon, SearchIcon, SparkleIcon, SettingsIcon, ShieldIcon, ConstructionIcon, LightningIcon } from '../../../components/CustomIcons'

const ITPage = ({ embedded = false, goBack }) => {
  const [services, setServices] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [retryAction, setRetryAction] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [view, setView] = useState('services') // services, projects, consultations, about
  const [selectedProject, setSelectedProject] = useState(null)
  const [showConsultationModal, setShowConsultationModal] = useState(false)
  const [showProjectModal, setShowProjectModal] = useState(false)
  const [showContactModal, setShowContactModal] = useState(false)
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    technology: '',
    price_range: ''
  })
  const [sortBy, setSortBy] = useState('created_at')
  const [consultationForm, setConsultationForm] = useState({
    name: '',
    email: '',
    description: ''
  })
  const [projectForm, setProjectForm] = useState({
    title: '',
    description: ''
  })
  const [consultationType, setConsultationType] = useState('')
  
  // Service request modal states
  const [showServiceModal, setShowServiceModal] = useState(false)
  const [selectedService, setSelectedService] = useState(null)
  
  // Notification state
  const [showAuthNotification, setShowAuthNotification] = useState(false)
  const [notificationMessage, setNotificationMessage] = useState('') // 'technical', 'expert', 'business'

  useEffect(() => {
    // Первоначальная загрузка
    loadData()
  }, [])

  useEffect(() => {
    // Перезагрузка при смене вида или фильтров
    if (view || currentPage > 1 || Object.values(filters).some(v => v) || sortBy !== 'created_at') {
      loadData()
    }
  }, [view, currentPage, filters, sortBy])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      if (view === 'services') {
        await loadServices()
      } else if (view === 'projects') {
        await loadProjects()
      }
    } catch (err) {
      setError(`Ошибка загрузки IT сервисов`)
      setRetryAction(() => loadData)
      console.error(err)
    } finally {
      setLoading(false)
    }
  }
  
  const handleRetry = () => {
    if (retryAction) {
      retryAction()
    }
  }

  const loadServices = async () => {
    const response = await getITServices({
      page: currentPage,
      search: filters.search,
      category: filters.category,
      technology: filters.technology,
      ordering: sortBy
    })
    setServices(response.data.results || response.data || [])
    setTotalPages(response.data.total_pages || 1)
  }

  const loadProjects = async () => {
    const response = await getITProjects({
      page: currentPage,
      search: filters.search,
      technology: filters.technology,
      ordering: sortBy
    })
    setProjects(response.data.results || response.data || [])
    setTotalPages(response.data.total_pages || 1)
  }



  const handleServiceOrder = (service) => {
    setSelectedService(service)
    setShowServiceModal(true)
  }
  
  const handleServiceRequestSuccess = (message) => {
    setShowServiceModal(false)
    setSelectedService(null)
    setNotificationMessage(message || 'Заявка успешно отправлена!')
    setShowAuthNotification(true)
    setTimeout(() => setShowAuthNotification(false), 4000)
  }
  
  const handleServiceRequestClose = () => {
    setShowServiceModal(false)
    setSelectedService(null)
  }

  const handleConsultationRequest = async (e) => {
    e.preventDefault()
    try {
      await createITProject({ ...consultationForm, type: 'consultation', consultation_type: consultationType })
      setShowConsultationModal(false)
      setConsultationForm({ name: '', email: '', description: '' })
      setConsultationType('')
      setNotificationMessage('Заявка на консультацию отправлена!')
      setShowAuthNotification(true)
      setTimeout(() => setShowAuthNotification(false), 4000)
    } catch (err) {
      console.error('Ошибка запроса консультации:', err)
      setNotificationMessage(`Ошибка: ${err.message}`)
      setShowAuthNotification(true)
      setTimeout(() => setShowAuthNotification(false), 4000)
    }
  }

  const handleProjectRequest = async (e) => {
    e.preventDefault()
    try {
      await createITProject(projectForm)
      setShowProjectModal(false)
      setProjectForm({ title: '', description: '' })
      setNotificationMessage('Заявка на проект отправлена!')
      setShowAuthNotification(true)
      setTimeout(() => setShowAuthNotification(false), 4000)
    } catch (err) {
      console.error('Ошибка запроса проекта:', err)
      setNotificationMessage(`Ошибка: ${err.message}`)
      setShowAuthNotification(true)
      setTimeout(() => setShowAuthNotification(false), 4000)
    }
  }

  // Функция применения фильтров
  const applyFilters = () => {
    setCurrentPage(1)
    loadData()
  }

  const getServiceIcon = (category) => {
    switch (category) {
      case 'development': return <ComputerIcon size={20} />
      case 'design': return <SparkleIcon size={20} />
      case 'consulting': return <PeopleIcon size={20} />
      case 'maintenance': return <ConstructionIcon size={20} />
      case 'security': return <ShieldIcon size={20} />
      case 'cloud': return <ComputerIcon size={20} />
      default: return <SettingsIcon size={20} />
    }
  }

  const getCategoryName = (category) => {
    switch (category) {
      case 'development': return 'Разработка'
      case 'design': return 'Дизайн'
      case 'consulting': return 'Консалтинг'
      case 'maintenance': return 'Поддержка'
      case 'security': return 'Безопасность'
      case 'cloud': return 'Облачные решения'
      default: return 'Другое'
    }
  }

  if (loading && (services.length === 0 && projects.length === 0)) {
    return (
      <div className={styles.loadingContainer}>
        <Loader size="large" />
        <p>Загрузка данных...</p>
      </div>
    )
  }

  return (
    <div className={styles.itPage}>
      {!embedded && (
        <div className={styles.header}>
          <h1 className={styles.pageTitle}>
            <span className={styles.icon}><ComputerIcon size={32} /></span>
            IT Услуги и Разработка
          </h1>
          <p className={styles.pageSubtitle}>
            Полный спектр IT-услуг: от разработки до технической поддержки
          </p>
        </div>
      )}

      {/* Статистика компании */}
      <div className={styles.statsSection}>
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statIcon}><StarIcon size={32} /></div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>150+</div>
              <div className={styles.statLabel}>Проектов выполнено</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}><StarIcon size={32} /></div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>50+</div>
              <div className={styles.statLabel}>Довольных клиентов</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}><CheckIcon size={32} /></div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>5</div>
              <div className={styles.statLabel}>Лет опыта</div>
            </div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statIcon}><PeopleIcon size={32} /></div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>15</div>
              <div className={styles.statLabel}>Специалистов</div>
            </div>
          </div>
        </div>
      </div>

      {/* Навигация */}
      <div className={styles.navigation}>
        <div className={styles.tabs}>
          <button 
            className={`${styles.tab} ${view === 'services' ? styles.active : ''}`}
            onClick={() => setView('services')}
          >
            Услуги
          </button>
          <button 
            className={`${styles.tab} ${view === 'projects' ? styles.active : ''}`}
            onClick={() => setView('projects')}
          >
            Портфолио
          </button>
          <button 
            className={`${styles.tab} ${view === 'consultations' ? styles.active : ''}`}
            onClick={() => setView('consultations')}
          >
            Консультации
          </button>
          <button 
            className={`${styles.tab} ${view === 'about' ? styles.active : ''}`}
            onClick={() => setView('about')}
          >
            О нас
          </button>
        </div>

        <div className={styles.actions}>
          <button 
            className={styles.consultationButton}
            onClick={() => setShowConsultationModal(true)}
          >
            Консультация
          </button>
          <button 
            className={styles.projectButton}
            onClick={() => setShowProjectModal(true)}
          >
            <PlusIcon size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />Заказать проект
          </button>
          <button 
            className={styles.contactButton}
            onClick={() => setShowContactModal(true)}
          >
            Связаться с нами
          </button>
        </div>
      </div>

      {/* Фильтры и поиск */}
      <div className={styles.filtersSection}>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder={`Поиск ${view === 'services' ? 'услуг' : view === 'projects' ? 'проектов' : 'консультаций'}...`}
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            className={styles.searchInput}
          />
          <button 
            onClick={applyFilters}
            className={styles.searchButton}
          >
            <SearchIcon size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />Найти
          </button>
        </div>
        
        <div className={styles.filtersRow}>
          {(view === 'services' || view === 'projects') && (
            <div className={styles.filterGroup}>
              <label>Категория:</label>
              <select 
                value={filters.category} 
                onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                className={styles.filterSelect}
              >
                <option value="">Все категории</option>
                <option value="development">Разработка</option>
                <option value="design">Дизайн</option>
                <option value="consulting">Консалтинг</option>
                <option value="maintenance">Поддержка</option>
                <option value="security">Безопасность</option>
                <option value="cloud">Облачные решения</option>
              </select>
            </div>
          )}
          
          {view === 'services' && (
            <div className={styles.filterGroup}>
              <label>Технология:</label>
              <select 
                value={filters.technology} 
                onChange={(e) => setFilters(prev => ({ ...prev, technology: e.target.value }))}
                className={styles.filterSelect}
              >
                <option value="">Все технологии</option>
                <option value="react">React</option>
                <option value="vue">Vue.js</option>
                <option value="angular">Angular</option>
                <option value="node">Node.js</option>
                <option value="python">Python</option>
                <option value="php">PHP</option>
              </select>
            </div>
          )}
          
          <div className={styles.filterGroup}>
            <label>Сортировка:</label>
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="created_at">По дате</option>
              <option value="name">По названию</option>
              {view === 'services' && <option value="price">По цене</option>}
              <option value="rating">По рейтингу</option>
            </select>
          </div>
          
          <button 
            onClick={applyFilters}
            className={styles.applyButton}
          >
            <SparkleIcon size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />Применить фильтры
          </button>
        </div>
      </div>

      {error && (
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}><ComputerIcon size={64} /></div>
          <div className={styles.errorContent}>
            <h3 className={styles.errorTitle}>Ошибка загрузки</h3>
            <p className={styles.errorMessage}>{error}</p>
            <button 
              onClick={handleRetry}
              className={styles.retryButton}
              disabled={loading}
            >
              {loading ? 'Загрузка...' : 'Попробовать снова'}
            </button>
          </div>
        </div>
      )}

      {/* Контент */}
      <div className={styles.content}>
        {view === 'services' && (
          <div className={styles.servicesGrid}>
            {services.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}><ComputerIcon size={64} /></div>
                <h3>Услуги временно недоступны</h3>
                <p>Мы работаем над обновлением каталога услуг</p>
              </div>
            ) : (
              services.map(service => (
                <div key={service.id} className={styles.serviceCard}>
                  <div className={styles.serviceHeader}>
                    <div className={styles.serviceIcon}>{getServiceIcon(service.category)}</div>
                    <div className={styles.categoryBadge}>{getCategoryName(service.category)}</div>
                  </div>
                  <h3>{service.title || service.name}</h3>
                  <p className={styles.serviceDescription}>{service.description}</p>
                  <div className={styles.serviceFooter}>
                    <div className={styles.servicePrice}>
                      {service.price ? `${service.price} сом` : 'Цена по запросу'}
                    </div>
                    <button 
                      className={styles.orderButton} 
                      onClick={() => handleServiceOrder(service)}
                    >
                      Заказать услугу
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {view === 'projects' && (
          <div className={styles.projectsGrid}>
            {projects.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>💼</div>
                <h3>Портфолио обновляется</h3>
                <p>Скоро здесь появятся наши лучшие проекты</p>
              </div>
            ) : (
              projects.map(project => (
                <div key={project.id} className={styles.projectCard}>
                  <h3>{project.title || project.name}</h3>
                  <p>{project.description}</p>
                  <p>Status: {project.status}</p>
                  <button 
                    className={styles.projectButton} 
                    onClick={() => setSelectedProject(project)}
                  >
                    View Project
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {view === 'consultations' && (
          <div className={styles.consultationsSection}>
            <div className={styles.consultationTypes}>
              <div className={styles.consultationType}>
                <div className={styles.typeHeader}>
                  <h3>📞 Техническая консультация</h3>
                  <span className={styles.price}>Бесплатно</span>
                </div>
                <p>Получите бесплатную консультацию по техническим вопросам</p>
                <ul>
                  <li>Анализ требований</li>
                  <li>Выбор технологий</li>
                  <li>Архитектурные решения</li>
                  <li>Оценка проекта</li>
                </ul>
                <button 
                  className={styles.orderButton}
                  onClick={() => {
                    setConsultationType('technical')
                    setShowConsultationModal(true)
                  }}
                >
                  Записаться
                </button>
              </div>

              <div className={styles.consultationType}>
                <div className={styles.typeHeader}>
                  <h3>
                    <SparkleIcon size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Экспертная консультация
                  </h3>
                  <span className={styles.price}>от 5 000 Сом</span>
                </div>
                <p>Глубокий анализ с экспертом и детальными рекомендациями</p>
                <ul>
                  <li>Аудит текущих решений</li>
                  <li>Стратегия развития</li>
                  <li>Подробный отчет</li>
                  <li>План внедрения</li>
                </ul>
                <button 
                  className={styles.orderButton}
                  onClick={() => {
                    setConsultationType('expert')
                    setShowConsultationModal(true)
                  }}
                >
                  Заказать
                </button>
              </div>

              <div className={styles.consultationType}>
                <div className={styles.typeHeader}>
                  <h3>🚀 Бизнес-консультация</h3>
                  <span className={styles.price}>от 15 000 Сом</span>
                </div>
                <p>Комплексная консультация по цифровой трансформации</p>
                <ul>
                  <li>Анализ бизнес-процессов</li>
                  <li>IT-стратегия</li>
                  <li>ROI расчеты</li>
                  <li>Roadmap проекта</li>
                </ul>
                <button 
                  className={styles.orderButton}
                  onClick={() => {
                    setConsultationType('business')
                    setShowConsultationModal(true)
                  }}
                >
                  Заказать
                </button>
              </div>
            </div>
          </div>
        )}

        {view === 'about' && (
          <div className={styles.aboutSection}>
            <div className={styles.companyInfo}>
              <h3>О компании NCORE IT</h3>
              <p>
                Мы команда профессиональных разработчиков и IT-консультантов. 
                Специализируемся на создании современных веб-приложений, мобильных решений и интеграции систем.
              </p>
              <div className={styles.advantages}>
                <div className={styles.advantage}>
                  <h4>
                    <SparkleIcon size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Индивидуальный подход
                  </h4>
                  <p>Каждый проект уникален, поэтому мы разрабатываем решения под ваши специфические задачи</p>
                </div>
                <div className={styles.advantage}>
                  <h4>
                    <LightningIcon size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Современные технологии
                  </h4>
                  <p>Используем актуальный стек технологий и следуем лучшим практикам разработки</p>
                </div>
                <div className={styles.advantage}>
                  <h4>🤝 Долгосрочное партнерство</h4>
                  <p>Предоставляем техническую поддержку и развитие проектов после их запуска</p>
                </div>
                <div className={styles.advantage}>
                  <h4>Прозрачное ценообразование</h4>
                  <p>Фиксированная стоимость и сроки, без скрытых платежей и переплат</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className={styles.loadingMore}>
            <Loader size="medium" />
          </div>
        )}

        {totalPages > 1 && (view === 'services' || view === 'projects') && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      {/* Модальные окна */}
      {showConsultationModal && (
        <div className={styles.consultationModal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>IT Консультация {consultationType === 'technical' ? '(Техническая)' : consultationType === 'expert' ? '(Экспертная)' : '(Бизнес)'}</h3>
              <button 
                className={styles.closeButton}
                onClick={() => {
                  setShowConsultationModal(false)
                  setConsultationType('')
                }}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>Запишитесь на консультацию с нашими IT специалистами.</p>
              <form onSubmit={handleConsultationRequest} className={styles.form}>
                <div className={styles.formGroup}>
                  <label>Ваше имя</label>
                  <input 
                    type="text" 
                    value={consultationForm.name}
                    onChange={(e) => setConsultationForm(prev => ({ ...prev, name: e.target.value }))}
                    required 
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Email</label>
                  <input 
                    type="email" 
                    value={consultationForm.email}
                    onChange={(e) => setConsultationForm(prev => ({ ...prev, email: e.target.value }))}
                    required 
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Опишите ваши IT потребности</label>
                  <textarea 
                    value={consultationForm.description}
                    onChange={(e) => setConsultationForm(prev => ({ ...prev, description: e.target.value }))}
                    required
                  ></textarea>
                </div>
                <button type="submit" className={styles.submitButton}>Отправить заявку</button>
              </form>
            </div>
          </div>
        </div>
      )}

      {showProjectModal && (
        <div className={styles.projectModal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Заказать проект</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setShowProjectModal(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>Закажите разработку индивидуального IT проекта.</p>
              <form onSubmit={handleProjectRequest} className={styles.form}>
                <div className={styles.formGroup}>
                  <label>Название проекта</label>
                  <input 
                    type="text" 
                    value={projectForm.title}
                    onChange={(e) => setProjectForm(prev => ({ ...prev, title: e.target.value }))}
                    required 
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Описание проекта</label>
                  <textarea 
                    value={projectForm.description}
                    onChange={(e) => setProjectForm(prev => ({ ...prev, description: e.target.value }))}
                    required
                  ></textarea>
                </div>
                <button type="submit" className={styles.submitButton}>Отправить заявку</button>
              </form>
            </div>
          </div>
        </div>
      )}

      {showContactModal && (
        <div className={styles.contactModal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Связаться с нами</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setShowContactModal(false)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>Свяжитесь с нашей IT командой.</p>
              <p>Email: ncore@gmail.com</p>
              <p>Телефон: +996600006032</p>
            </div>
          </div>
        </div>
      )}

      {selectedProject && (
        <div className={styles.projectViewModal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>{selectedProject.title || selectedProject.name}</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setSelectedProject(null)}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>{selectedProject.description}</p>
              <p>Status: {selectedProject.status}</p>
              <p>Technologies: {selectedProject.technologies}</p>
            </div>
          </div>
        </div>
      )}

      {/* Service Request Modal */}
      {showServiceModal && selectedService && (
        <ServiceRequestModal
          isOpen={showServiceModal}
          service={selectedService}
          onClose={handleServiceRequestClose}
          onSubmit={async (requestData) => {
            try {
              const { createServiceRequest } = await import('../../../service/ITService')
              await createServiceRequest(selectedService.id || selectedService.slug, requestData)
              handleServiceRequestSuccess('Заявка успешно отправлена! Мы свяжемся с вами в ближайшее время.')
            } catch (error) {
              console.error('Error creating service request:', error)
              throw error
            }
          }}
        />
      )}

      {/* Auth Notification */}
      {showAuthNotification && (
        <div className={styles.authNotification}>
          <div className={styles.notificationContent}>
            <span className={styles.notificationIcon}><CheckIcon size={20} /></span>
            <span className={styles.notificationMessage}>{notificationMessage}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default ITPage